import React from 'react';

import type { LunaClient } from '@vlabs/api-bindings/src';
import { apiContainer } from '@vlabs/api-bindings/src';
import { routes } from '@vlabs/shared/navigation/routes';
import { selectAppServices } from '@vlabs/shared/selectors/appSelectors';
import { isVersionGte } from '@vlabs/shared/utils';
import { useModal } from '@vlabs/uikit';
import qs from 'qs';
import { useSelector } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';

import { TARGET_CONFIG } from './defaultValues';
import { getDetectorTypeFromTargets } from './getDetectorTypeFromTargets';
import type { TTargetKey, TVideoCheckFormDefaultValues, TVideoCheckFormSubmitValues } from './types';
import { VideoChecksForm } from './VideoChecksForm';

interface TClickableControl {
  onClick: VoidFunction;
}

interface UseVideoCheckFormReturn {
  wrap: (Component: React.JSXElementConstructor<TClickableControl>) => React.JSX.Element;
}

const buildTargetsPayload = (
  checks: Partial<Record<TTargetKey, boolean>>,
  lpVersion?: { major?: number; minor?: number; patch?: number },
): string[] => {
  const result = new Set<string>(['overview']);

  (Object.entries(checks) as [TTargetKey, boolean][]).forEach(([key, isEnabled]) => {
    if (!isEnabled) return;

    const config = TARGET_CONFIG[key];
    if (!config) return;

    config.forEach((target) => {
      if (
        target === 'aggregated_face_detection_sources'
        && !isVersionGte(lpVersion, { major: 5, minor: 143, patch: 0 })
      ) {
        return;
      }

      result.add(target);
    });
  });

  return Array.from(result);
};

function useVideoCheckForm(formProps?: TVideoCheckFormDefaultValues): UseVideoCheckFormReturn {
  const modal = useModal();
  const history = useHistory();

  const appServices = useSelector(selectAppServices);
  const lpVersion = appServices?.lunaPlatform?.versionNum;

  const lunaClient = (apiContainer.lunaClient as LunaClient);

  const onSubmit = async (values: TVideoCheckFormSubmitValues) => {
    const { fileList, parameters, ...checks } = values;

    const { data } = await lunaClient.objects.create(fileList[0], {
      headers: {
        'Content-Type': (fileList[0] as File).type,
      },
    });

    const resp = await lunaClient.videosdk.create({
      video: {
        url: data.external_url,
        orientation: { angle: 0 },
        pts: { start_time: 0 },
        error_handling: { action: 'fail' },
      },
      analytics: [{
        name: 'human_tracking',
        targets: buildTargetsPayload(checks, lpVersion),
        parameters: {
          ...parameters,
          tracking: {
            ...parameters.tracking,
            detector_type: getDetectorTypeFromTargets(checks),
          },
        },
      }],
      deferred_task: {
        enable: 1,
        storage_policy: { storage_type: 'luna_image_store' },
      },
    });

    const objectId = data.object_id;
    const route = generatePath(routes.checks.videoResults, { taskId: resp?.task_id });
    const queryParams = qs.stringify({ ...checks, filename: fileList[0]?.name, objectId });
    modal.close();
    history.push(`${route}?${queryParams}`);
  };

  return {
    wrap: (Component: React.JSXElementConstructor<TClickableControl>): React.JSX.Element => {
      return (
        <React.Fragment>
          {modal.wrap(<VideoChecksForm
            {...formProps}
            onCancel={modal.close}
            onSubmit={onSubmit}
          />)}
          <Component onClick={modal.open} />
        </React.Fragment>
      );
    },
  };
}

export { useVideoCheckForm };
